import React from "react";
import { useTranslation } from "react-i18next";
import {
  EmailBuilderBoundedRange,
  EmailBuilderColorOverrideField,
} from "../../shell/panel/blockSettings/EmailBuilderBlockSettingsShared";
import { EmailBuilderSettingsPaddingSection } from "../controls/EmailBuilderSettingsPaddingSection";
import { SettingSection } from "../controls/SettingSection";
import { TextCaseControl } from "../controls/TextCaseControl";
import {
  baselineEyebrowFontSize,
  baselineEyebrowPadding,
  baselineEyebrowTextTransform,
} from "../../domain/baselines";
import { isPaddingInactive } from "../settingsSectionActivation";
import { DEFAULT_EYEBROW_RULE_COLOR } from "../../domain/blocks/eyebrow";
import {
  resolveEyebrowRuleColor,
  resolveEyebrowTextColor,
} from "../../domain/text-theme";
import type { EyebrowBlock, EyebrowRule } from "../../domain/types";
import type { BlockSettingsProps } from "./types";
import { useEmailBuilderBlockContrastAgainst } from "../useEmailBuilderBlockSurfaceColor";
import { useStableDomId } from "../../useStableDomId";

const RULE_OPTIONS: EyebrowRule[] = ["none", "before", "after", "both"];
const RULE_LABELS: Record<EyebrowRule, string> = {
  none: "None",
  before: "Before",
  after: "After",
  both: "Both",
};

function EyebrowRuleControl({
  value,
  onChange,
}: {
  value: EyebrowRule;
  onChange: (rule: EyebrowRule) => void;
}) {
  const { t } = useTranslation();
  const labelId = useStableDomId("email-builder-eyebrow-rule-label");

  return (
    <div className="email-builder-settings-inline-field">
      <div id={labelId} className="email-builder-settings-inline-field__label">
        {t("Line")}
      </div>
      <div className="email-builder-settings-inline-field__control">
        <div
          className="email-builder-settings-segment-wide"
          role="radiogroup"
          aria-labelledby={labelId}
        >
          {RULE_OPTIONS.map(rule => (
            <button
              key={rule}
              type="button"
              role="radio"
              aria-checked={value === rule}
              data-on={value === rule ? "true" : "false"}
              onClick={() => onChange(rule)}
            >
              {t(RULE_LABELS[rule])}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function EyebrowBlockSettings({
  block,
  patch,
  textTheme,
  rowId,
  columnId,
}: BlockSettingsProps & { block: EyebrowBlock }) {
  const { t } = useTranslation();
  const fontSize = block.fontSize ?? baselineEyebrowFontSize();
  const textTransform = block.textTransform ?? baselineEyebrowTextTransform();
  const blockContrastAgainst = useEmailBuilderBlockContrastAgainst(rowId, columnId);

  return (
    <>
      <SettingSection sectionId="layout" title={t("Layout")}>
        <EyebrowRuleControl
          value={block.rule}
          onChange={rule => patch({ rule })}
        />
      </SettingSection>

      <SettingSection
        sectionId="padding"
        title={t("Padding")}
        activation={{
          mode: "optional",
          isActive: !isPaddingInactive(block.padding),
          onAdd: () => patch({ padding: { ...baselineEyebrowPadding() } }),
        }}
      >
        <EmailBuilderSettingsPaddingSection
          fieldLabel={t("Eyebrow padding (px)")}
          value={block.padding}
          defaultValue={baselineEyebrowPadding()}
          onChange={padding => {
            patch({ padding: { ...padding } });
          }}
        />
      </SettingSection>

      <SettingSection sectionId="appearance" title={t("Appearance")}>
        <EmailBuilderBoundedRange
          label={t("Font size (px)")}
          value={fontSize}
          min={8}
          max={24}
          defaultValue={baselineEyebrowFontSize()}
          onChange={nextFontSize => {
            if (nextFontSize === undefined) {
              return;
            }
            patch({ fontSize: nextFontSize });
          }}
        />
        <TextCaseControl
          value={textTransform}
          onChange={nextTextTransform => patch({ textTransform: nextTextTransform })}
        />
        <EmailBuilderColorOverrideField
          showFieldLabel={false}
          label={t("Text")}
          color={resolveEyebrowTextColor(block, textTheme)}
          hasOverride={block.color != null}
          contrastAgainst={blockContrastAgainst}
          contrastLargeText={fontSize >= 18}
          onChange={color => {
            patch({
              color: color === textTheme.headingColor ? null : color,
            });
          }}
          onReset={() => patch({ color: null })}
        />
        <EmailBuilderColorOverrideField
          showFieldLabel={false}
          label={t("Line color")}
          color={resolveEyebrowRuleColor(block, textTheme)}
          hasOverride={block.ruleColor != null}
          contrastAgainst={blockContrastAgainst}
          onChange={color => {
            patch({
              ruleColor: color === DEFAULT_EYEBROW_RULE_COLOR ? null : color,
            });
          }}
          onReset={() => patch({ ruleColor: null })}
        />
      </SettingSection>
    </>
  );
}
